export type Disposition = "for" | "against" | "neutral" | "";

export interface AgendaItem {
  id: number;
  agenda: string;
  title: string;
}

export interface ItemComment {
  comment: string;
  files: File[];
  disposition: Disposition;
}

export interface FieldOption {
  id: number;
  name: string;
}

export interface FormField {
  id: number;
  name: string;
  required: boolean;
  // UserCustomAttributeType: 0 Text, 1 Boolean, 2 Numeric, 3 DateTime, 4 List
  attributeType?: number;
  // Present (and rendered as a dropdown) only for list-type attributes.
  attributeOptions?: FieldOption[] | null;
}

// UserCustomAttributeType.List — list attributes render as a <select>.
export const ATTRIBUTE_TYPE_LIST = 4;

export type CommentPosition = "favor" | "oppose" | null;

// Display modes the admin configures per meeting (mapped from CommentSectionOptions on the server).
export interface CommentModes {
  identity: "named" | "anonymous";
  upvotes: boolean;
  replies: boolean;
  flag: boolean;
  positionTags: boolean;
  civilityFilter: boolean;
}

// A file attached to a comment, as returned for public display.
export interface CommentAttachment {
  // PollCommentAttachmentTypes: 0 Image, 1 Video, 2 Document
  type: number;
  fileName: string;
  // Download/source URL (image src for images, document download link otherwise).
  url: string;
}

export interface PublicReply {
  id: number;
  authorName: string;
  isStaff: boolean;
  createdAtUtc: string;
  body: string;
  isMine: boolean;
  attachments?: CommentAttachment[];
}

export interface PublicComment {
  id: number;
  authorName: string;
  isStaff: boolean;
  createdAtUtc: string;
  position: CommentPosition;
  votes: number;
  votedByMe: boolean;
  // Held by the automated civility filter — visible only to its author, recorded on the record.
  pending: boolean;
  isMine: boolean;
  // Submitted privately by the user (visible only to the author and staff).
  isPrivate?: boolean;
  body: string;
  replies: PublicReply[];
  attachments?: CommentAttachment[];
}

export interface PublicCommentConfig {
  offlineEventId: number;
  customerId: number;
  projectId: number;
  commentDisplayMode: number;
  collectDisposition: boolean;
  headerMessage: string;
  fields: FormField[];
  agendaItems: AgendaItem[];
  antiForgeryToken: string;
  meetingName: string;
  meetingDate: string;
  allowAttachments: boolean;
  attachmentsLimitCount: number;
  attachmentsLimitSize: number;
  attachmentTypesAllowed: string[];
  commentCharacterLimit: number;
  modes: CommentModes;
  commentsArePublic: boolean;
  // When true, residents may opt to keep a comment private (visible only to staff/themselves).
  // Maps to CommentSectionOptions.AllowParticipantToTogglePublicComment.
  allowPrivateComments: boolean;
  // Label for the staff/admin reply badge — the agency (customer) name.
  staffBadgeLabel: string;
}
